import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState } from 'react';
import {
  SynechronGithubPickerFormData,
  SynechronGithubPickerOptions,
} from '../types';
import { getPickerErrors, parseRepoPickerUrl } from '../validation';
import { useFetchUserTeams } from './hooks/useFetchUserTeams';

const ALLOWED_DOMAINS = [
  'AI',
  'Cloud & DevOps',
  'CTO Office',
  'Data & Analytics',
  'Digital',
  'Engineering',
  'Finlabs',
  'Synechron Maintainers',
  'Salesforce',
  'Strategy, Transformation & Architecture',
  'No Owner',
];

export const SynechronGithubPicker = ({
  formData,
  onChange,
  uiSchema,
  rawErrors,
}: FieldExtensionComponentProps<string>) => {
  const uiOptions = uiSchema?.['ui:options'] as SynechronGithubPickerOptions;
  const { repo, team, domain } = useMemo(
    () => parseRepoPickerUrl(formData),
    [formData],
  );

  // Track which fields have been touched by the user
  const [touchedFields, setTouchedFields] = useState({
    repo: false,
    team: false,
    domain: false,
  });

  const realtimeErrors = useMemo(
    () => getPickerErrors({ repo, team, domain }),
    [repo, team, domain],
  );

  // Only show errors if there are rawErrors (form submission) OR if specific fields have been touched
  const showErrors = rawErrors && rawErrors.length > 0;

  const {
    teams,
    loading: teamsLoading,
    error: teamsFetchError,
  } = useFetchUserTeams();

  const handleChangeData = useCallback(
    (newData: Partial<SynechronGithubPickerFormData>) => {
      const searchParams = new URLSearchParams({
        repo: repo || '',
        team: team || '',
        domain: domain || '',
      });

      Object.entries(newData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.set(key, value);
        }
      });

      // Format: github.com?owner=synechron&repo=xxx&team=xxx&domain=xxx
      onChange(`github.com?owner=synechron&${searchParams.toString()}`);
    },
    [onChange, repo, team, domain],
  );

  const handleRepoBlur = useCallback(() => {
    setTouchedFields(prev => ({ ...prev, repo: true }));
  }, []);

  const handleTeamChange = useCallback(
    (newValue: string | null) => {
      setTouchedFields(prev => ({ ...prev, team: true }));
      handleChangeData({ team: newValue ?? '' });
    },
    [handleChangeData],
  );

  const handleDomainChange = useCallback(
    (newValue: string | null) => {
      setTouchedFields(prev => ({ ...prev, domain: true }));
      handleChangeData({ domain: newValue ?? '' });
    },
    [handleChangeData],
  );

  return (
    <FormControl margin="normal" fullWidth>
      <Stack spacing={2}>
        {/* Repository Name Input */}
        <TextField
          margin="normal"
          label={uiOptions?.repoInputTitle ?? 'Repository Name'}
          required
          helperText={
            uiOptions?.repoInputDescription ??
            'The name for the GitHub repository in the synechron organization'
          }
          value={repo}
          onChange={e => handleChangeData({ repo: e.target.value })}
          onBlur={handleRepoBlur}
          error={
            (showErrors || touchedFields.repo) &&
            (!repo || !/^[a-z0-9-]+$/.test(repo))
          }
        />

        {/* Team Selection */}
        {teamsFetchError ? (
          <FormHelperText error>
            Error loading teams: {teamsFetchError.message}
          </FormHelperText>
        ) : (
          <Autocomplete
            value={team || null}
            onChange={(_, newValue) => handleTeamChange(newValue)}
            options={teams.map(t => t.slug)}
            getOptionLabel={option => {
              const teamData = teams.find(t => t.slug === option);
              return teamData ? `${teamData.name} (${teamData.slug})` : option;
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={uiOptions?.teamInputTitle ?? 'Team'}
                required
                helperText={
                  uiOptions?.teamInputDescription ??
                  'Select a team to assign to this repository'
                }
                error={(showErrors || touchedFields.team) && !team}
              />
            )}
            loading={teamsLoading}
            loadingText={
              <Stack alignItems="center" direction="row" gap={2}>
                <CircularProgress size="1.5rem" />
                <Typography variant="body1">Loading your teams...</Typography>
              </Stack>
            }
            autoSelect
          />
        )}

        {/* Domain Selection */}
        <Autocomplete
          value={domain || null}
          onChange={(_, newValue) => handleDomainChange(newValue)}
          options={ALLOWED_DOMAINS}
          renderInput={params => (
            <TextField
              {...params}
              label={uiOptions?.domainInputTitle ?? 'Domain'}
              required
              helperText={
                uiOptions?.domainInputDescription ??
                'Select the domain for this repository'
              }
              error={(showErrors || touchedFields.domain) && !domain}
            />
          )}
          autoSelect
        />

        {/* Show validation errors only after form submission */}
        {showErrors && realtimeErrors.length > 0 && (
          <FormHelperText error>
            {rawErrors?.[0] || realtimeErrors[0]}
          </FormHelperText>
        )}
      </Stack>
    </FormControl>
  );
};
